import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import {
  MdAttachMoney,
  MdAccountBalance,
  MdTrendingUp,
  MdTrendingDown,
  MdWarning,
  MdCheckCircle
} from 'react-icons/md';
import './Dashboard.css';

interface FinancialData {
  accountsReceivable: number;
  accountsPayable: number;
  cashBalance: number;
  salesThisMonth: number;
  purchasesThisMonth: number;
}

interface BankAccount {
  id: number;
  name: string;
  balance: number;
  currency: string;
}

interface PendingTask {
  id: number;
  title: string;
  type: string;
  amount: number;
  dueDate: string;
}

const Dashboard: React.FC = () => {
  const [financialData, setFinancialData] = useState<FinancialData>({
    accountsReceivable: 0,
    accountsPayable: 0,
    cashBalance: 0,
    salesThisMonth: 0,
    purchasesThisMonth: 0
  });

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);

  useEffect(() => {
    // TODO: Fetch data from API
    // Mock data for now
    setFinancialData({
      accountsReceivable: 2500000,
      accountsPayable: 1800000,
      cashBalance: 5000000,
      salesThisMonth: 3200000,
      purchasesThisMonth: 1500000
    });

    setBankAccounts([
      { id: 1, name: 'BDO - Main Account', balance: 3500000, currency: 'PHP' },
      { id: 2, name: 'BPI - Payroll Account', balance: 1200000, currency: 'PHP' },
      { id: 3, name: 'Metrobank - USD Account', balance: 25000, currency: 'USD' }
    ]);

    setPendingTasks([
      { id: 1, title: 'Supplier Payment - ABC Materials', type: 'payment', amount: 350000, dueDate: '2025-10-05' },
      { id: 2, title: 'Invoice Approval - Customer XYZ', type: 'approval', amount: 180000, dueDate: '2025-10-03' },
      { id: 3, title: 'Payroll Processing - October 1st', type: 'payroll', amount: 850000, dueDate: '2025-10-01' }
    ]);
  }, []);

  const formatCurrency = (amount: number, currency: string = 'PHP') => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <MainLayout>
      <div className="dashboard">
        <div className="dashboard-header">
          <h2>Dashboard</h2>
          <p className="subtitle">Welcome to DEWFOAM Accounting & Payroll System</p>
        </div>

        {/* Financial Snapshot */}
        <div className="section">
          <h3 className="section-title">Financial Snapshot</h3>
          <div className="card-grid">
            <div className="stat-card blue">
              <div className="stat-icon">
                <MdTrendingUp />
              </div>
              <div className="stat-content">
                <p className="stat-label">Accounts Receivable</p>
                <h3 className="stat-value">{formatCurrency(financialData.accountsReceivable)}</h3>
              </div>
            </div>

            <div className="stat-card orange">
              <div className="stat-icon">
                <MdTrendingDown />
              </div>
              <div className="stat-content">
                <p className="stat-label">Accounts Payable</p>
                <h3 className="stat-value">{formatCurrency(financialData.accountsPayable)}</h3>
              </div>
            </div>

            <div className="stat-card green">
              <div className="stat-icon">
                <MdAccountBalance />
              </div>
              <div className="stat-content">
                <p className="stat-label">Cash & Bank Balance</p>
                <h3 className="stat-value">{formatCurrency(financialData.cashBalance)}</h3>
              </div>
            </div>

            <div className="stat-card purple">
              <div className="stat-icon">
                <MdAttachMoney />
              </div>
              <div className="stat-content">
                <p className="stat-label">Sales This Month</p>
                <h3 className="stat-value">{formatCurrency(financialData.salesThisMonth)}</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="row-grid">
          {/* Bank Accounts */}
          <div className="section flex-1">
            <h3 className="section-title">Bank Account Balances</h3>
            <div className="card">
              <div className="bank-accounts-list">
                {bankAccounts.map(account => (
                  <div key={account.id} className="bank-account-item">
                    <div className="bank-info">
                      <MdAccountBalance className="bank-icon" />
                      <div>
                        <p className="bank-name">{account.name}</p>
                        <p className="bank-currency">{account.currency}</p>
                      </div>
                    </div>
                    <div className="bank-balance">
                      {formatCurrency(account.balance, account.currency)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pending Tasks */}
          <div className="section flex-1">
            <h3 className="section-title">Pending Tasks</h3>
            <div className="card">
              <div className="pending-tasks-list">
                {pendingTasks.map(task => (
                  <div key={task.id} className="pending-task-item">
                    <div className="task-icon">
                      {task.type === 'approval' ? (
                        <MdCheckCircle className="icon-success" />
                      ) : (
                        <MdWarning className="icon-warning" />
                      )}
                    </div>
                    <div className="task-info">
                      <p className="task-title">{task.title}</p>
                      <p className="task-meta">
                        Due: {task.dueDate} · {formatCurrency(task.amount)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sales & Purchase Trend */}
        <div className="section">
          <h3 className="section-title">Sales & Purchase Trend</h3>
          <div className="card">
            <div className="chart-placeholder">
              <p>Chart will be displayed here</p>
              <p className="chart-note">Monthly sales and purchase trend graph</p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
